import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { BezpartHeader } from "../components/BezpartHeader";
import { apiFetch, type MeResponse } from "../api/client";
import { mainSiteBoardLoginUrl } from "../lib/mainSiteLinks";
import { usePageMeta } from "../lib/pageMeta";
import { boardAuthMessage } from "../lib/boardAuthMessages";

/**
 * Закрытый вход для онлайн-школы Bezpart.
 * Не индексируется и не ссылается с публичной витрины — вход через портал bez-part.ru.
 */
export function SchoolPage() {
  /**
   * Контур текущей сессии. В кабинет отсюда пускаем только школьный аккаунт:
   * внешняя сессия на школьной странице — не повод считать вход выполненным.
   */
  const [sessionKind, setSessionKind] = useState<"school" | "external" | null>(null);
  const [ready, setReady] = useState(false);
  const [params] = useSearchParams();
  /** Сообщение о сорвавшемся SSO-входе: callback возвращает сюда с ?board_auth=… */
  const authError = boardAuthMessage(params.get("board_auth"));

  usePageMeta({
    title: "Вход для школы · Bezpart Доска",
    robots: "noindex, nofollow",
  });

  useEffect(() => {
    document.body.classList.add("wb-page--home", "wb-page--school-entry");
    return () => {
      document.body.classList.remove("wb-page--home", "wb-page--school-entry");
    };
  }, []);

  useEffect(() => {
    void apiFetch<MeResponse>("/api/v1/me")
      .then((me) => setSessionKind(me.account_kind === "school" ? "school" : "external"))
      .catch(() => setSessionKind(null))
      .finally(() => setReady(true));
  }, []);

  if (ready && sessionKind === "school") {
    return <Navigate to="/cabinet" replace />;
  }

  return (
    <>
      <BezpartHeader
        mode="guest"
        loginHref={mainSiteBoardLoginUrl("/cabinet")}
        logoHref="/school"
      />

      <div className="wb-shell wb-shell--home">
        <main className="wb-main wb-main--home wb-main--school">
          <header className="wb-home-hero">
            <div className="wb-home-hero__glow" aria-hidden="true" />
            <p className="wb-home-hero__eyebrow">Онлайн-школа Bezpart</p>
            <h1 className="wb-h1 wb-home-hero__title">Доска для занятий</h1>
            <p className="wb-lead wb-home-hero__lead">
              Вход только через аккаунт школы на bez-part.ru. Без тарифов и отдельной
              регистрации — доски доступны в рамках вашего школьного аккаунта.
            </p>
            {authError ? (
              <p className="wb-alert wb-alert--error" role="alert">
                {authError}
              </p>
            ) : null}
          </header>

          <section className="wb-home-cta" aria-labelledby="wb-school-cta-title">
            <h2 id="wb-school-cta-title" className="wb-home-cta__title">
              Вход для преподавателей и учеников
            </h2>
            <div className="wb-cards wb-cards--home wb-cards--school">
              <div className="wb-card wb-card--account">
                <div className="wb-card__badge wb-card__badge--accent">Для школы</div>
                <h2 className="wb-h2">Войти через портал</h2>
                <p className="wb-card__intro">
                  Откроется вход на bez-part.ru. После авторизации вы попадёте в кабинет досок.
                </p>
                <ul className="wb-card__list">
                  <li>Папки и список досок без лимитов тарифа</li>
                  <li>Совместная работа и ссылки для учеников</li>
                  <li>Без оплаты тарифа на стороне доски</li>
                </ul>
                {!ready ? (
                  <p className="wb-card__intro">Проверка сессии…</p>
                ) : sessionKind === "external" ? (
                  <>
                    <p className="wb-alert wb-alert--error" role="alert">
                      Сейчас вы вошли под внешним аккаунтом доски. Школьный кабинет из него
                      недоступен — выйдите и войдите через bez-part.ru.
                    </p>
                    <a
                      className="wb-btn wb-btn--ghost wb-btn--lg"
                      href="/cabinet"
                    >
                      Вернуться во внешний кабинет
                    </a>
                  </>
                ) : (
                  <a
                    className="wb-btn wb-btn--primary wb-btn--lg"
                    href={mainSiteBoardLoginUrl("/cabinet")}
                  >
                    Войти через bez-part.ru
                  </a>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
